import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIsFetching } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { FormValues, STEP_ID, validation } from './DafAccount.meta';
import {
  AddAccountSelectionModalWrapper,
  Selection,
} from '../../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { useNavigation } from '../../hooks/useNavigation';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { VisualPicker } from '../../../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { formatCurrency } from '../../../../../CommonUtils/formatNumber';
import { queryKeys } from '../../api/queryKeys';
import { FullPageLoading } from '../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { StepLoading } from '../../components/StepLoading/StepLoading';
import { AccountDTO, useDafAccountsQuery } from '../../api/useDafAccountsQuery';
import { useClientId } from '../../../../../_hooks/useClientId';
import { StepErrorState } from '../../components/StepErrorState/StepErrorState';
import { AccountModalUsageContext } from '../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import { WorkflowDTO } from '../../api/useCharitableGivingQuery';

export const DafAccount = () => {
  const { workflow } = useCharitableGivingContext();

  const {
    data: { goalId },
  } = workflow;

  const { isLoading, isError, refetch, data } = useDafAccountsQuery(goalId);

  if (isLoading) {
    return <StepLoading />;
  }

  if (isError) {
    return <StepErrorState onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  return <Content dafAccounts={data} goalId={goalId} />;
};

interface ContentProps {
  dafAccounts: AccountDTO[];
  goalId: string | null;
}

const Content = ({ dafAccounts, goalId }: ContentProps) => {
  const clientId = useClientId();

  const isFetchingDafAccounts = useIsFetching(queryKeys.dafAccounts(clientId, goalId)) > 0;

  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const [isOpenAddAccountModal, setIsOpenAddAccountModal] = useState(false);

  const { control, handleSubmit, setValue, formState } = useForm<FormValues>({
    defaultValues: {
      dafAccount: workflow.data.dafAccount,
    },
    resolver: yupResolver(validation),
  });

  const initialSelectedDafAccount = useRef(workflow.data.dafAccount).current;

  /**
   * Effect to allow auto-selecting the added account
   */
  const dafAccountsRef = useRef(dafAccounts.map(({ id }) => id));
  useEffect(() => {
    if (dafAccounts.length !== dafAccountsRef.current.length) {
      const newDafAccount =
        dafAccounts.find(({ id }) => !dafAccountsRef.current.includes(id))?.id || null;

      setValue('dafAccount', newDafAccount);

      dafAccountsRef.current = dafAccounts.map(({ id }) => id);
    }
  }, [dafAccounts, setValue]);

  const onSubmit = handleSubmit(values => {
    const foundDafAccount = dafAccounts.find(({ id }) => id === values.dafAccount);

    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        dafAccount: values.dafAccount,
        // Reset DAF-related fields to the new account's values when switching accounts
        ...(!initialSelectedDafAccount || values.dafAccount !== initialSelectedDafAccount
          ? {
              dafDonationStartYear: foundDafAccount?.donationStartYear ?? null,
              dafDonationEndYear: foundDafAccount?.donationEndYear ?? null,
              donationAmount: foundDafAccount?.donationAmount ?? null,
              dafGrantStartYear: foundDafAccount?.grantStartYear ?? null,
              dafGrantEndYear: foundDafAccount?.grantEndYear ?? null,
              dafGrantAmount: foundDafAccount?.grantAmount ?? null,
              kindOfProperty: foundDafAccount?.kindOfProperty ?? null,
              fundAccounts: foundDafAccount?.fundAccounts,
            }
          : {}),
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const AddAccountButton = (
    <Button variant="link" leftIcon="fa fa-plus" onClick={() => setIsOpenAddAccountModal(true)}>
      Add a DAF Account
    </Button>
  );

  return (
    <>
      {isFetchingDafAccounts && <FullPageLoading />}

      <StepContainer
        alert={formState.errors.dafAccount?.message}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text color="neutral.700" fontSize="h3" fontWeight={500}>
              Select DAF account
            </Text>

            {dafAccounts.length > 0 && AddAccountButton}
          </Box>
        }
        onSubmit={onSubmit}
        footer={
          <StepFooter
            isFirstStep={isFirstStep}
            progress={progress}
            onPrevious={previousStep ? () => setStep(previousStep) : undefined}
          />
        }
      >
        {dafAccounts.length === 0 && (
          <Box
            display="grid"
            style={{ placeItems: 'center' }}
            borderTop="1px solid"
            borderColor="neutral.200"
            pt="32"
            mx={-32}
          >
            {AddAccountButton}
          </Box>
        )}

        {dafAccounts.length > 0 && (
          <Controller
            control={control}
            name="dafAccount"
            render={({ field: { value: dafAccount, onChange } }) => (
              <Box display="flex" flexDirection="column" mt="16" css="gap: 16px">
                {dafAccounts.map(({ id, name, balance, institution }) => (
                  <VisualPicker
                    key={id}
                    variant="radio"
                    label={name}
                    helperText={institution ?? undefined}
                    rightText={formatCurrency(balance)}
                    onChange={() => onChange(id)}
                    checked={dafAccount === id}
                  />
                ))}
              </Box>
            )}
          />
        )}
      </StepContainer>

      {isOpenAddAccountModal && (
        <AddAccountSelectionModalWrapper
          initialSelection={Selection.Manual}
          usageContext={AccountModalUsageContext.DafAccounts}
          showExternalProviders={false}
          onClose={() => setIsOpenAddAccountModal(false)}
        />
      )}
    </>
  );
};
